import React from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { webhooksKey } from '@dashboard/lib/queryKeys';
import * as Yup from 'yup';
import Callout from '@/dashboard/components/Callout';
import CopyableText from '@/dashboard/components/CopyableText';

const WebhookDeleteDialog = ({ open, onClose, webhook }) => {
  const { sellerId, token } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const deleteWebhook = useMutation({
    mutationFn: hardDelete => {
      const queryParams = hardDelete ? '?hard_delete=true' : '';
      return fetcher(`/sellers/${sellerId}/webhooks/${webhook?.id}${queryParams}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhooksKey(sellerId) });
      emitToast({
        title: 'Webhook deleted',
        type: 'success',
      });
      onClose();
    },
    onError: () => {
      emitToast({
        title: 'Failed to delete webhook',
        type: 'error',
      });
    },
  });

  return (
    <Dialog
      title="Delete webhook"
      open={open}
      onCancel={onClose}
      cancelButton={null}
      confirmButton={null}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Are you sure you want to delete the webhook <b>{webhook?.name}</b>? This action cannot be
          undone and will immediately stop all webhook deliveries.
        </p>

        {webhook && <CopyableText text={webhook.url} label="Webhook endpoint"></CopyableText>}

        <Form
          showState={false}
          validationSchema={Yup.object().shape({
            webhookName: Yup.string()
              .required('Webhook name is required')
              .test(
                'matches-webhook-name',
                'The webhook name entered does not match.',
                function (value) {
                  return value === webhook?.name;
                },
              ),
            hardDelete: Yup.boolean(),
          })}
          initialValues={{
            webhookName: '',
            hardDelete: false,
          }}
          onSubmit={async values => {
            try {
              await deleteWebhook.mutateAsync(values.hardDelete);
              return { success: true };
            } catch (error) {
              return { error };
            }
          }}
          fields={({ values }) => [
            {
              name: 'webhookName',
              label: (
                <>
                  Type <b>{webhook?.name}</b> to confirm deletion
                </>
              ),
              type: 'text',
              required: true,
              placeholder: webhook?.name,
            },
            {
              name: 'hardDelete',
              label: 'Permanently delete',
              type: 'checkbox',
            },
            {
              type: 'custom',
              visible: values.hardDelete === true,
              renderField: () => (
                <Callout variant="warning" padded={false}>
                  <Callout.Heading>Permanently deleting your webhook will</Callout.Heading>
                  <Callout.Content>
                    <ul className="list-disc space-y-1">
                      <li>Stop all future webhook deliveries to this endpoint</li>
                      <li>Remove the webhook from your dashboard</li>
                      <li className="font-medium">Permanently delete all delivery history</li>
                    </ul>
                  </Callout.Content>
                </Callout>
              ),
            },
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Delete webhook',
            appearance: 'secondary',
            disabled: deleteWebhook.isPending,
          }}
          cancelButton={{
            label: 'Cancel',
            appearance: 'secondary',
            onClick: onClose,
          }}
        />
      </div>
    </Dialog>
  );
};

export default WebhookDeleteDialog;
